import { Component, OnInit } from '@angular/core';
import { firstValueFrom, Observable } from 'rxjs';
import {
  Address,
  UserSchema,
  PAYMENT_COMPS,
  PaymentStepsMap,
  PaymentStepsGenerator,
} from 'src/app/core';

import {
  Order,
  Voucher,
  PaymentEntity,
  PaymentTokenUrl,
  PaymentStepperNewEvent,
  PaymentFinalizeInterface,
  PaymentTransportationMethod,
} from 'src/app/shared';

import { SubSink } from 'subsink';
import { TitleService } from '..';
import { PaymentService } from './services';
import { ActivatedRoute } from '@angular/router';
import { stepFadeInAnimation } from './utilities';
import { MatSnackBar } from '@angular/material/snack-bar';
import { IsLoadingService } from '@service-work/is-loading';

@Component({
  templateUrl: './payment.component.html',
  styleUrls: ['./payment.component.scss'],

  animations: [stepFadeInAnimation],
})
export class PaymentComponent implements OnInit {
  public shoppingCartOrders: Order[] = [];
  public componentsName: string[] = [];
  public isLoggedIn: boolean = false;
  public payment!: PaymentEntity;
  public selectedTabIndex = 0;
  public address!: Address;
  public user!: UserSchema;

  /**
   * Navigate user to this url when it's ready
   * it should be generated in server.
   */
  public selectedVoucher!: Voucher;
  public paymentRedirectUrl!: string;
  public PAYMENT_COMPS = PAYMENT_COMPS;
  public activatedComponent!: PAYMENT_COMPS;
  public paymentEndingIndex = PaymentStepsMap.size;
  public initComponent: PAYMENT_COMPS = PAYMENT_COMPS.TRANSPORTATION;

  // --------------------------------------------------Private properties|

  public transportationMethods: PaymentTransportationMethod[] = [];
  public transportationMethod!: PaymentTransportationMethod;

  private subsink = new SubSink();
  private stepsIterator = PaymentStepsGenerator();

  // --------------------------------------------------Public setters & getters

  public get loading$(): Observable<boolean> {
    return this.isLoadingService.isLoading$({ key: 'payment' });
  }

  // --------------------------------------------------Constructor & Injections|

  constructor(
    private isLoadingService: IsLoadingService,
    private activatedRoute: ActivatedRoute,
    private paymentService: PaymentService,
    private titleService: TitleService,
    private snackbar: MatSnackBar
  ) {
    // Pipe data from resolvers into component.
    this.subsink.sink = this.activatedRoute.data.subscribe((data) => {
      this.transportationMethods = data['transportationMethods'];
      this.shoppingCartOrders = data['orders'];
      this.isLoggedIn = !!data['user'];
      this.user = data['user'];

      // Resume payment

      if (data['payment']) {
        this.payment = data['payment'];
        this.selectedTabIndex = 3;
      }
    });

    this.titleService.setTitle('صفحه پرداخت');
  }

  ngOnInit(): void {
    this.handle({ name: 'next' });
  }

  // --------------------------------------------------Public methods|

  /**
   * This method resumes finalized and old payment created
   * previously.
   */
  public async resume() {
    const params = await firstValueFrom(this.activatedRoute.params);
    if (params['resume'] && params['paymentId']) {
      alert('resuming');
    }
  }

  setVoucher(voucher: Voucher) {
    this.selectedVoucher = voucher;
  }

  /**
   * Handles the events coming from step components
   * defined inside payment module.
   *
   * @param event Event coming from steps.
   */
  handle(event: PaymentStepperNewEvent) {
    switch (event.name) {
      case 'navigate':
        this.activatedComponent = event.payload;
        break;

      case 'next':
        if (event?.comp === PAYMENT_COMPS.SELECT_ORDERS) {
          this.payment = event.payload;
          this.selectedTabIndex++;
        }

        /**
         * Checks for passing into ending component
         */
        if (event.comp === PAYMENT_COMPS.FINAL_FACTOR) {
          if (!this.paymentRedirectUrl) {
            this.snackbar.open('امکان رفتن به مرحله بعد نیست', '', {
              duration: 1000,
            });

            break;
          }
        }

        const nextStep = this.stepsIterator.next().value;
        this.activatedComponent = nextStep || PAYMENT_COMPS.TRANSPORTATION;

        break;

      case 'finalize':
        this.initLoading();

        this.subsink.sink = this.finalize().subscribe((data) => {
          this.payment = data;
          this.setUrl();
          this.selectedTabIndex++;
        });

        break;

      case 'redirect':
        const url = event.payload;

        if (!url) {
          throw new Error('URL is not defined on redirect event type');
        }

        window.location.href = url;
    }
  }

  /**
   * We get url from API and set it to related
   * property.
   */
  public setUrl(): void {
    this.subsink.sink = this.getPaymentToken().subscribe((data) => {
      this.paymentRedirectUrl = data.redirect_url;
      this.activatedComponent = PAYMENT_COMPS.ENDING;
      this.endLoading();
    });
  }

  public initLoading(): void {
    this.isLoadingService.add({ key: 'payment' });
  }

  public endLoading(): void {
    while (this.isLoadingService.isLoading({ key: 'payment' })) {
      this.isLoadingService.remove({ key: 'payment' });
    }
  }

  // ----------------------------------------Private methods|

  /**
   * Gather information and send request to
   * api and get back payment information.
   */
  private finalize(): Observable<PaymentEntity> {
    try {
      const readyToFinalize = this.checkPaymentReadyForFinalization();

      if (!readyToFinalize) {
        throw new Error('Not ready to finalize the payment');
      }

      const paymentId = this.payment.id;
      const finalizeObject = this.getFinalizeObject();
      return this.paymentService.finalizePayment(paymentId, finalizeObject);
    } catch (e) {
      this.snackbar.open('متاسفانه امکان نهایی کردن سفارش نسیت', '', {
        duration: 1500,
      });

      throw e;
    }
  }

  /**
   * returns information needed for finalizing payment object.
   * @returns {PaymentFinalizeInterface}
   */
  private getFinalizeObject(): PaymentFinalizeInterface {
    const finalizeObject: PaymentFinalizeInterface = {
      addressId: this.address.id,
      transportationId: this.transportationMethod.id,
    };

    if (this?.selectedVoucher?.id) {
      finalizeObject.voucherCodeId = this.selectedVoucher.id;
    }

    return finalizeObject;
  }

  /**
   * Please check if payment is finalized.
   */
  private checkPaymentReadyForFinalization(): boolean {
    if (!!!this.payment) return false;
    if (!!!this.address) return false;
    if (!!!this.transportationMethod) return false;

    return true;
  }

  private getPaymentToken(): Observable<PaymentTokenUrl> {
    const paymentId = this.payment.id;
    return this.paymentService.getPaymentToken(paymentId);
  }

  ngOnDestroy() {
    this.subsink.unsubscribe();
    this.isLoadingService.clear();
  }
}
